import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { assetSubResourceBreadcrumb } from '../../../../../../../utils/breadcrumb.utils';
import { MenuItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { manualMeasureDefinition } from '../../../../../../../entity-definitions/manual-measure.definition';
import { ManualMeasuresService } from '../../../../../../../services/api/generated/manual-measures/manual-measures.service';
import type { ManualMeasureCreationInput } from '../../../../../../../services/api/generated/model';
import { AnalysisPostStore } from '../../../../../../../stores/analysis-post.store';
import { AssetStore } from '../../../../../../../stores/asset.store';
import { AuscultationInformationStore } from '../../../../../../../stores/auscultation-info.store';
import { LocationStore } from '../../../../../../../stores/location.store';
import { ManualMeasureStore } from '../../../../../../../stores/manual-measure.store';
import { MeasureTypeStore } from '../../../../../../../stores/measure-type.store';
import { AutomaticMeasureStore } from '../../../../../../../stores/automatic-measure.store';
import { UnitStore } from '../../../../../../../stores/unit.store';
import { CheckerType } from '../../../../../../../types/measure-checker.interface';
import { CesAssetBreadcrumbComponent } from '../../../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesWizardStepperComponent } from '../../../../../../ui/ces-wizard-stepper/ces-wizard-stepper.component';
import { BaseMeasureWizardComponent } from '../../shared/base-measure-wizard.component';
import { filterMeasuresByAsset } from '../../shared/measure-asset-filter';
import { ManualMeasureFormulaComponent } from './manual-measure-formula.component';
import { ManualMeasureInfoComponent } from './manual-measure-info.component';
import { ManualMeasureVerificationComponent } from './manual-measure-verification.component';
import { FormulaParameter } from '../../../../../../../types/formula.interface';
import { ToastService } from '../../../../../../../services/toast.service';
import { isConflictWithCode } from '../../../../../../../utils/http-error.utils';
import { MEASURE_FORMULA_REQUIRED, MEASURE_NAME_ALREADY_EXISTS } from '../../../../../../../utils/measure-error-codes';
import { extractServerErrorCode } from '../../formula-period';
import { startOfDay } from '../../../../../../../utils/date-range-filter.utils';
import { UniqueNameEntry } from '../../shared/base-measure-info.component';
import { formulaCompletionValidator, verificationCompletionValidator } from '../../shared/measure-step-validators';

interface ManualMeasureInfoFormValue {
  name: string;
  measureTypeId: number | null;
  unitId: number | null;
  locationIds: number[];
  priority: boolean;
}

interface ManualMeasureFormulaFormValue {
  startTs: string;
  parameters: FormulaParameter[];
  expression: string;
}

interface ManualMeasureVerificationFormValue {
  startTs: string;
  type: CheckerType;
  negativeGap: number;
  positiveGap: number;
  firstParameters: FormulaParameter[];
  firstExpression: string;
  secondParameters: FormulaParameter[];
  secondExpression: string;
}

interface ManualMeasureDeviceFormValue {
  deviceId: number | null;
  deviceSettings: string;
}

interface ManualMeasureWizardFormValue {
  info: ManualMeasureInfoFormValue;
  formula: ManualMeasureFormulaFormValue;
  verification: ManualMeasureVerificationFormValue;
  device: ManualMeasureDeviceFormValue;
}

@Component({
  selector: 'app-manual-measure-wizard',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesAssetBreadcrumbComponent,
    CesWizardStepperComponent,
    ManualMeasureFormulaComponent,
    ManualMeasureInfoComponent,
    ManualMeasureVerificationComponent,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  templateUrl: './manual-measure-wizard.component.html',
})
export class ManualMeasureWizardComponent extends BaseMeasureWizardComponent implements OnInit {
  protected readonly manualMeasureStore = inject(ManualMeasureStore);
  protected readonly automaticMeasureStore = inject(AutomaticMeasureStore);
  protected readonly analysisPostStore = inject(AnalysisPostStore);
  protected readonly auscultationInfoStore = inject(AuscultationInformationStore);
  protected readonly measureTypeStore = inject(MeasureTypeStore);
  protected readonly unitStore = inject(UnitStore);
  protected readonly locationsStore = inject(LocationStore);
  protected readonly assetStore = inject(AssetStore);
  private readonly wizardApi = inject(ManualMeasuresService);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  protected readonly stepKeys = ['info', 'formula', 'verification'] as const;

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return assetSubResourceBreadcrumb(this.translateService, {
      parentLabelKey: 'navigation.measures',
      parentRouterLink: ['/assets', this.currentAssetId, 'configuration', 'measures', 'manual'],
      currentLabel: this.translateService.translate(manualMeasureDefinition.messages.dialogTitles.create()),
    });
  });

  readonly measuresForAsset = computed(() => filterMeasuresByAsset(this.manualMeasureStore.entities(), this.currentAssetId));

  readonly existingNamesOnAsset = computed<readonly UniqueNameEntry[]>(() => {
    const assetId = this.currentAssetId;
    if (!assetId) return [];
    const sources = [
      ...this.manualMeasureStore.entities(),
      ...this.automaticMeasureStore.entities(),
      ...this.analysisPostStore.entities(),
      ...this.auscultationInfoStore.entities(),
    ];
    const seen = new Set<string>();
    const result: UniqueNameEntry[] = [];
    for (const entity of sources) {
      const record = entity as { assetId?: number; name?: string | null };
      if (record.assetId !== assetId) continue;
      const name = record.name;
      if (!name) continue;
      const key = name.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ name });
    }
    return result;
  });

  get infoGroup(): FormGroup {
    return this.wizardForm.get('info') as FormGroup;
  }

  get formulaGroup(): FormGroup {
    return this.wizardForm.get('formula') as FormGroup;
  }

  get verificationGroup(): FormGroup {
    return this.wizardForm.get('verification') as FormGroup;
  }

  // Pré-remplit les débuts de validité (formule + vérification) à minuit local plutôt qu'à l'heure courante.
  private defaultStartTs(): string {
    return startOfDay(new Date()).toISOString();
  }

  override ngOnInit(): void {
    this.wizardForm = this.fb.group({
      info: this.fb.group({
        name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
        measureTypeId: [null, Validators.required],
        unitId: [null, Validators.required],
        locationIds: [[]],
        priority: [false],
      }),
      formula: this.fb.group(
        {
          startTs: [this.defaultStartTs(), Validators.required],
          parameters: [[] as FormulaParameter[]],
          expression: [''],
        },
        { validators: [formulaCompletionValidator] },
      ),
      verification: this.fb.group(
        {
          startTs: [this.defaultStartTs(), Validators.required],
          type: [CheckerType.NONE, Validators.required],
          negativeGap: [-10],
          positiveGap: [10],
          firstParameters: [[] as FormulaParameter[]],
          firstExpression: [''],
          secondParameters: [[] as FormulaParameter[]],
          secondExpression: [''],
        },
        { validators: [verificationCompletionValidator] },
      ),
      device: this.fb.group({
        deviceId: [null],
        deviceSettings: [''],
      }),
    });

    super.ngOnInit();
  }

  protected async loadInitialData(): Promise<void> {
    try {
      if (this.currentAssetId) {
        await Promise.all([
          this.assetStore.loadById(this.currentAssetId),
          this.manualMeasureStore.getByAssetId(this.currentAssetId),
          this.automaticMeasureStore.getByAssetId(this.currentAssetId),
          this.analysisPostStore.getByAssetId(this.currentAssetId),
          this.auscultationInfoStore.getByAssetId(this.currentAssetId),
          this.measureTypeStore.loadAll(),
          this.unitStore.loadAll(),
          this.locationsStore.getLocationsByAsset(this.currentAssetId),
        ]);
      }
    } catch (error) {
      console.error(this.translateService.translate('common.errors.dataLoadFailed'), error);
    }
  }

  protected async submitForm(): Promise<void> {
    if (this.wizardForm.invalid || this.submitting) {
      this.wizardForm.markAllAsTouched();
      console.error(this.translateService.translate('common.errors.formInvalid'));
      return;
    }

    this.submitting = true;
    const formData = this.wizardForm.getRawValue() as ManualMeasureWizardFormValue;
    const { info, formula, verification, device } = formData;

    try {
      const measureData: Record<string, unknown> = {
        name: info.name,
        measureTypeId: info.measureTypeId,
        unitId: info.unitId,
        assetId: this.currentAssetId,
        locationIds: info.locationIds,
        priority: info.priority,
        formulaStartTs: formula.startTs,
        formulaParameters: formula.parameters,
        formulaExpression: formula.expression,
        deviceId: device.deviceId,
        deviceSettings: device.deviceSettings,
      };

      if (verification.type !== CheckerType.NONE) {
        measureData.verificationType = verification.type;
        measureData.verificationStartTs = verification.startTs;

        if (verification.type === CheckerType.TWO_FIXED_LIMITS) {
          measureData.negativeGap = verification.negativeGap;
          measureData.positiveGap = verification.positiveGap;
        }
        if (verification.type === CheckerType.TWO_POLYNOMIALS) {
          measureData.formulaFirstParameters = verification.firstParameters;
          measureData.formulaFirstExpression = verification.firstExpression;
          measureData.formulaSecondParameters = verification.secondParameters;
          measureData.formulaSecondExpression = verification.secondExpression;
        }
        if (verification.type === CheckerType.POLYNOMIAL_AND_RANGES) {
          measureData.formulaFirstParameters = verification.firstParameters;
          measureData.formulaFirstExpression = verification.firstExpression;
          measureData.negativeGap = verification.negativeGap;
          measureData.positiveGap = verification.positiveGap;
        }
      }

      const result = await firstValueFrom(this.wizardApi.postManualMeasureList(measureData as unknown as ManualMeasureCreationInput));

      if (result?.id) {
        void this.router.navigate(['../../../measures/manual', result.id], { relativeTo: this.route });
      } else {
        void this.router.navigate(['../..'], { relativeTo: this.route });
      }
    } catch (error) {
      const messageKey = this.resolveSubmitErrorKey(error);
      this.toastService.error(messageKey, undefined, undefined, undefined, { name: info.name });
      console.error(this.translateService.translate('common.errors.saveFailed'), error);
    } finally {
      this.submitting = false;
    }
  }

  private resolveSubmitErrorKey(error: unknown): string {
    if (extractServerErrorCode(error) === MEASURE_FORMULA_REQUIRED) {
      return 'entities.measureFormula.messages.formulaRequired';
    }
    return isConflictWithCode(error, MEASURE_NAME_ALREADY_EXISTS)
      ? 'entities.manualMeasure.messages.duplicateError'
      : 'common.errors.saveFailed';
  }

  getTitle(): string {
    const name = this.wizardForm.get('info.name')?.value as string | null;
    const base = this.translateService.translate(manualMeasureDefinition.messages.dialogTitles.create());
    const trimmed = name?.trim();
    return trimmed && trimmed.length >= 3 ? `${base} : ${trimmed.substring(0, 50)}` : base;
  }
}
